import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import {
  AbstractControl,
  FieldArray,
  FieldControl,
  FieldGroup,
  FormArray,
  FormBuilder,
  Validators
} from 'react-reactive-form';
import { AppState } from '../../../../store/store.types';
import { PromoCodeActionTypes } from '../../types/promocode.types';
import { addNewPromoCode, deletePromoCode, editPromoCode, getPromoCode } from '../../actions/promocode.actions';
import { TextInput } from '../../../../components/forms/Input';
import RadioButton from '../../../../components/forms/RadioButton';
import Checkbox from '../../../../components/forms/Checkbox';
import StatusLabel from '../../../../components/forms/StatusLabel';

import { PromoCodeBlackoutPeriod, promoCodeEditRoles, PromoCodeModel } from '../../../../api/pricing/pricing.types';
import { DATE_FORMAT, StatusType } from '../../../../api/api.types';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import { BrokerRateModel, CarClassModel, DeskModel } from '../../../../api/core/core.types';
import Breadcrumbs from '../../../../components/breadcrumbs/Breadcrumbs';
import { getDeskList } from '../../../setup/actions/desk.actions';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import { TwoColumnsSelectValueModel } from '../../../../components/forms/TwoColumnsSelect';
import { MultiSelect } from '../../../../components/forms/MultiSelect';
import { getBrokerRates, getBrokers, getCarClassesList, getLocationsList } from '../../../../helpers/listview.helper';
import CalendarDateRangePicker, { DateRangeInputNew } from '../../../../components/forms/CalendarDateRangePicker';
import { dateOrderCorrect, formatDateTime } from '../../../../helpers/date.helper';
import dayjs, { Dayjs } from 'dayjs';
import { WithTranslation, withTranslation } from 'react-i18next';
import { getAllCarClasses } from '../../../core/actions/carClass.actions';
import SectionTitle from '../../../../components/forms/SectionTitle';
import { getBrokerRateList } from '../../../setup/actions/brokerrate.actions';
import infoIcon from '../../../../assets/img/info-circle.svg';
import { UncontrolledTooltip as Tooltip } from 'reactstrap';
import { SupplierBrokerModel, SupplierModel } from '../../../../api/admin/admin.types';
import { Link } from 'react-router-dom';
import { getSupplierBrokerList } from '../../../admin/actions/supplierbroker.actions';

interface PromoCodeLocationProps {
  code: string;
}

interface BlackoutPeriod {
  validFrom?: Dayjs;
  validTo?: Dayjs;
  id: number;
}

interface PromoCodeDetailsProps extends RouteComponentProps<PromoCodeLocationProps>, WithTranslation {
  selectedPromoCode: PromoCodeModel | null;
  getSelectedPromoCode: (code: string) => Promise<any>;
  editPromoCode: (code: string, request: PromoCodeModel) => void;
  addNewPromoCode: (request: PromoCodeModel) => void;
  deletePromoCode: (code: string) => void;
  getAllCarClasses: () => void;
  getDeskList: () => void;
  getBrokerList: () => void;
  getBrokerRateList: () => void;
  isNewCode?: boolean;
  postSuccess: boolean | null;
  carClasses: CarClassModel[];
  desks: DeskModel[];
  brokers: SupplierBrokerModel[];
  brokerRates: BrokerRateModel[];
  lastFetch: number | null;
  user: UserModel | null;
  supplier: SupplierModel | null;
}

interface PromoCodeDetailsState {
  isModalOpen: boolean;
  isEdit: boolean;
  selectedCarClasses: any[];
  selectedLocations: string[];
  selectedBrokers: number[];
  selectedBrokerRates: string[];
  blackoutPeriods: BlackoutPeriod[];
}

class PromoCodeDetails extends React.Component<PromoCodeDetailsProps, PromoCodeDetailsState> {

  readonly state = {
    isModalOpen: false,
    isEdit: false,
    selectedCarClasses: [],
    selectedLocations: [],
    selectedBrokers: [],
    selectedBrokerRates: [],
    blackoutPeriods: [] as BlackoutPeriod[]
  };

  form = FormBuilder.group({
    promoCode: [ '', Validators.required ],
    discountPercentage: [ null, [ Validators.required, Validators.min(0), Validators.max(100) ] ],
    comment: [ '' ],
    pickupFrom: [ null, Validators.required ],
    pickupTo: [ null, Validators.required ],
    requestFrom: [ null, Validators.required ],
    requestTo: [ null, Validators.required ],
    daysFrom: [ null, [ Validators.required, Validators.min(1) ] ],
    daysTo: [ null, [ Validators.required, Validators.min(1) ] ],
    locationsSelection: [ 'all' ],
    carClassSelection: [ 'all' ],
    brokerSelection: [ 'all' ],
    brokerRateSelection: [ 'all' ],
    locations: [ { items: [], selected: [] } ],
    carClasses: [ { items: [], selected: [] } ],
    brokers: [ { items: [], selected: [] } ],
    brokerRates: [ { items: [], selected: [] } ],
    active: [ true ],
    blackoutPeriods: FormBuilder.array([])
  });

  async componentDidMount() {
    const { code } = this.props.match.params;

    if (!this.props.carClasses.length) {
      this.props.getAllCarClasses();
    }
    if (!this.props.desks.length) {
      this.props.getDeskList();
    }

    if (!this.props.brokerRates.length) {
      this.props.getBrokerRateList();
    }

    if (!this.props.brokers.length) {
      this.props.getBrokerList();
    }

    if (code) {
      await this.props.getSelectedPromoCode(code);
    }

    if (this.props.carClasses.length) {
      this.assignCarClassesToForm(this.props.carClasses);
    }
    if (this.props.desks.length) {
      this.assignDesksToForm(this.props.desks);
    }

    if (this.props.brokers.length) {
      this.assignBrokersToForm(this.props.brokers);
    }

    if (this.props.brokerRates.length) {
      this.assignBrokerRatesToForm(this.props.brokerRates);
    }

    const blackoutPeriods: BlackoutPeriod[] = [];
    if (this.props.selectedPromoCode?.blackoutPeriods.length) {
      this.props.selectedPromoCode.blackoutPeriods.sort((b1, b2) => {
        return dayjs(b1.validFrom).isBefore(b2.validFrom) ? -1 : dayjs(b1.validFrom).isAfter(b2.validFrom) ? 1 : 0;
      }).forEach((blackoutPeriod: PromoCodeBlackoutPeriod, id: number) => {
        blackoutPeriods.push({
          validFrom: dayjs(blackoutPeriod.validFrom),
          validTo: dayjs(blackoutPeriod.validTo),
          id: id
        });
      });
    }
    this.setState({ blackoutPeriods: blackoutPeriods });
  }

  componentDidUpdate(prevProps: PromoCodeDetailsProps) {
    if ((this.props.isNewCode) && this.props.postSuccess) {
      this.props.history.push(`/rates/promocodes/${this.form.controls.promoCode.value}/view`);
    }
    if (this.props.selectedPromoCode && (!prevProps.selectedPromoCode || this.props.selectedPromoCode !== prevProps.selectedPromoCode)) {
      this.updateFormValue();
    }

    if (this.props.carClasses.length !== prevProps.carClasses.length) {
      this.assignCarClassesToForm(this.props.carClasses);
    }

    if (this.props.desks.length !== prevProps.desks.length) {
      this.assignDesksToForm(this.props.desks);
    }

    if (this.state.blackoutPeriods.length) {
      this.assignBlackoutPeriodsToForm(this.state.blackoutPeriods);
    }

    if (this.props.brokers.length !== prevProps.brokers.length) {
      this.assignBrokersToForm(this.props.brokers);
    }

    if (this.props.brokerRates.length !== prevProps.brokerRates.length) {
      this.assignBrokerRatesToForm(this.props.brokerRates);
    }

    if (this.props.supplier) {
      if (!this.props.selectedPromoCode?.daysTo) {
        this.form.patchValue({ daysFrom: 1, daysTo: this.props.supplier?.maxRentalDuration });
      }
    }
  }

  updateFormValue = () => {
    if (!this.props.selectedPromoCode) {
      return;
    }

    this.form.patchValue({
      promoCode: this.props.selectedPromoCode.code,
      discountPercentage: this.props.selectedPromoCode.discountPercentage * 100,
      comment: this.props.selectedPromoCode.comment,
      pickupFrom: dayjs(this.props.selectedPromoCode.pickupFrom),
      pickupTo: dayjs(this.props.selectedPromoCode.pickupTo),
      requestFrom: dayjs(this.props.selectedPromoCode.requestFrom),
      requestTo: dayjs(this.props.selectedPromoCode.requestTo),
      daysFrom: this.props.selectedPromoCode.daysFrom ? this.props.selectedPromoCode.daysFrom : 1,
      daysTo: this.props.selectedPromoCode.daysTo,
      locationsSelection: this.props.selectedPromoCode.desks.length ? 'selection' : 'all',
      carClassSelection: this.props.selectedPromoCode.carClasses.length ? 'selection' : 'all',
      brokerSelection: this.props.selectedPromoCode.brokers.length ? 'selection' : 'all',
      brokerRateSelection: this.props.selectedPromoCode.brokerRates.length ? 'selection' : 'all',
      locations: (!!this.props.selectedPromoCode && this.props.selectedPromoCode.desks) ? {
        ...this.form.controls.locations.value,
        selected: this.props.selectedPromoCode.desks.map((c) => c.deskCode)
      } : this.form.controls.locations.value,
      carClasses: (!!this.props.selectedPromoCode && this.props.selectedPromoCode.carClasses) ? {
        ...this.form.controls.carClasses.value,
        selected: this.props.selectedPromoCode.carClasses
          .map((c) => c.acrissCode)
      } : this.form.controls.carClasses.value,
      brokers: this.props.selectedPromoCode ? {
        ...this.form.controls.brokers.value,
        selected: this.props.selectedPromoCode.brokers.map((b) => b.id)
      } : this.form.controls.brokers.value,
      brokerRates: this.props.selectedPromoCode ? {
        ...this.form.controls.brokerRates.value,
        selected: this.props.selectedPromoCode.brokerRates.map((p) => p.brokerRateCode)
      } : this.form.controls.brokerRates.value,
      active: this.props.selectedPromoCode.statusType === StatusType.ACTIVE
    });

    this.setState({
      selectedCarClasses: this.props.selectedPromoCode.carClasses.map((c) => c.acrissCode),
      selectedLocations: this.props.selectedPromoCode.desks.map((c) => c.deskCode)
    });
  };

  assignDesksToForm = (desks: DeskModel[]) => {
    const form = this.form.controls.locations.value as TwoColumnsSelectValueModel;
    desks.forEach((desk) => {
      const obj: any = { ...desk };
      obj.deskCode = desk.code;
      form.items.push(obj);
    });

    if (this.props.selectedPromoCode) {
      this.props.selectedPromoCode.desks.forEach((d) => {
        form.selected.push(d.deskCode);
      });
      this.setState({
        selectedLocations: this.props.selectedPromoCode.desks.map((d) => d.deskCode)
      });
    }
  };

  assignBrokersToForm = (brokers: SupplierBrokerModel[]) => {
    const form = this.form.controls.brokers.value as TwoColumnsSelectValueModel;
    brokers.forEach((broker) => {
      const obj: any = { ...broker };
      obj.brokerId = broker.id;
      form.items.push(obj);
    });

    if (this.props.selectedPromoCode) {
      this.props.selectedPromoCode.brokers.forEach((b) => {
        form.selected.push(b.id);
      });
      this.setState({
        selectedBrokers: this.props.selectedPromoCode.brokers.map((b) => b.id)
      });
    }
  };

  assignBrokerRatesToForm = (brokerRate: BrokerRateModel[]) => {
    const form = this.form.controls.brokerRates.value as TwoColumnsSelectValueModel;
    brokerRate.forEach((p) => {
      const obj: any = { ...p };
      obj.brokerRateCode = p.code;
      form.items.push(obj);
    });

    if (this.props.selectedPromoCode) {
      form.selected = this.props.selectedPromoCode.brokerRates.map((p) => p.brokerRateCode);
      this.setState({
        selectedBrokerRates: this.props.selectedPromoCode.brokerRates.map((p) => p.brokerRateCode)
      });
    }
  };

  assignCarClassesToForm = (carClasses: CarClassModel[]) => {
    const form = this.form.controls.carClasses.value as TwoColumnsSelectValueModel;
    const acrissCodesAdded: string[] = [];
    carClasses.forEach((carClass) => {
      if (!acrissCodesAdded.find((ac) => ac === carClass.acrissCode)) {
        form.items.push(carClass);

        acrissCodesAdded.push(carClass.acrissCode);
      }
    });
    if (this.props.selectedPromoCode) {
      this.props.selectedPromoCode.carClasses.forEach((c) => {
        form.selected.push(c.acrissCode);
      });
      this.setState({
        selectedCarClasses: this.props.selectedPromoCode.carClasses
          .map((c) => c.acrissCode)
      });
    }
  };

  onAllCarClassesSelect = () => {
    this.form.controls.carClasses.patchValue({ selected: [] });
    this.setState({ selectedCarClasses: [] });
  };

  onAllBrokersSelect = () => {
    this.form.controls.brokers.patchValue({ selected: [] });
    this.setState({ selectedBrokers: [] });
  };

  onAllBrokerRatesSelect = () => {
    this.form.controls.brokerRates.patchValue({ selected: [] });
    this.setState({ selectedBrokerRates: [] });
  };


  onAllLocationsSelect = () => {
    this.form.controls.locations.setValue({ ...this.form.controls.locations.value, selected: [] });
    this.setState({ selectedLocations: [] });
  };

  handleCarClassSelectValueChange = (v: any) => {
    this.setState({ selectedCarClasses: v.map((itm: any) => itm.acrissCode) });
  };

  handleLocationSelectValueChange = (v: any) => {
    this.setState({ selectedLocations: v.map((itm: any) => itm.deskCode) });
  };

  handleBrokerRateValueChange = (v: any) => {
    this.setState({ selectedBrokerRates: v.map((itm: any) => itm.brokerRateCode) });
  };

  handleBrokerValueChange = (v: any) => {
    this.setState({ selectedBrokers: v.map((itm: any) => itm.id) });
  };

  onDateRangeChange = (range: DateRangeInputNew, field: string) => {
    dateOrderCorrect(range, field, this.form);
    this.form.patchValue({
      [ `${field}From` ]: range.startDate,
      [ `${field}To` ]: range.endDate
    });
  };

  onBlackoutDateRangeChange = (range: DateRangeInputNew, index: number) => {
    const blackoutPeriods: BlackoutPeriod[] = this.state.blackoutPeriods;
    blackoutPeriods.forEach((b) => {
      if (b.id === index) {
        b.validFrom = range.startDate;
        b.validTo = range.endDate;
      }
    });

    this.setState({ blackoutPeriods: blackoutPeriods });
  };

  getBlackoutPeriods = () => {
    const { t } = this.props;
    const blackoutPeriods: BlackoutPeriod[] = this.state.blackoutPeriods;
    const blackoutPeriodsList: JSX.Element[] = [];

    if (blackoutPeriods.length) {
      blackoutPeriods.forEach((b) => {
        blackoutPeriodsList.push(
          <div className="d-flex row info-container">
            <div className="col-4"/>
            <div className="form-control-static">
              {dayjs(b.validFrom).format('DD.MM.YYYY')}
              {' - '}
              {dayjs(b.validTo).format('DD.MM.YYYY')}
            </div>
          </div>
        );
      });

      return blackoutPeriodsList;
    }
    return (
      <div className="d-flex row info-container">
        <div className="col-4"/>
        <div className="form-control-static text-muted">
          {t('text.promo_code.no_blackout_periods_view')}
        </div>
      </div>
    );

  };

  onDeleteConfirm = async () => {
    const { code } = this.props.match.params;
    await this.props.deletePromoCode(code);
  };

  onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const form = this.form.controls;
    this.form.markAsSubmitted();
    if (!dateOrderCorrect({ startDate: form.requestFrom.value, endDate: form.requestTo.value },
      'request', this.form) || !dateOrderCorrect({
      startDate: form.pickupFrom.value, endDate:
      form.pickupTo.value
    }, 'pickup', this.form)) {
      return;
    }
    if (this.form.invalid) {
      return;
    }
    if (this.props.selectedPromoCode) {
      const request: PromoCodeModel = {
        ...this.props.selectedPromoCode,
        comment: form.comment.value,
        requestFrom: form.requestFrom.value.format(DATE_FORMAT),
        requestTo: form.requestTo.value.format(DATE_FORMAT),
        discountPercentage: form.discountPercentage.value / 100,
        pickupFrom: form.pickupFrom.value.format(DATE_FORMAT),
        pickupTo: form.pickupTo.value.format(DATE_FORMAT),
        daysFrom: form.daysFrom.value,
        daysTo: form.daysTo.value,
        desks: this.state.selectedLocations.map((v: string) => ({ deskCode: v })),
        carClasses: this.state.selectedCarClasses.map((v: string) => ({ acrissCode: v })),
        brokers: this.state.selectedBrokers.map((v: number) => ({ id: v })),
        brokerRates: this.state.selectedBrokerRates.map((v: string) => ({ brokerRateCode: v })),
        statusType: form.active.value ? StatusType.ACTIVE : StatusType.INACTIVE,
        blackoutPeriods: form.blackoutPeriods.value
      };
      const { code } = this.props.match.params;

      this.props.editPromoCode(code, request);
      this.setState({ isEdit: false });
    } else {
      const request: PromoCodeModel = {
        code: form.promoCode.value,
        comment: form.comment.value,
        discountPercentage: form.discountPercentage.value / 100,
        pickupFrom: form.pickupFrom.value.format(DATE_FORMAT),
        pickupTo: form.pickupTo.value.format(DATE_FORMAT),
        requestFrom: form.requestFrom.value.format(DATE_FORMAT),
        requestTo: form.requestTo.value.format(DATE_FORMAT),
        daysFrom: form.daysFrom.value,
        daysTo: form.daysTo.value,
        desks: this.state.selectedLocations.map((v: string) => ({ deskCode: v })),
        carClasses: this.state.selectedCarClasses.map((v: string) => ({ acrissCode: v })),
        brokers: this.state.selectedBrokers.map((v: number) => ({ id: v })),
        brokerRates: this.state.selectedBrokerRates.map((v: string) => ({ brokerRateCode: v })),
        statusType: form.active.value ? StatusType.ACTIVE : StatusType.INACTIVE,
        blackoutPeriods: form.blackoutPeriods.value
      };

      this.props.addNewPromoCode(request);
    }
  };

  onCancel = () => {
    if (this.props.isNewCode) {
      this.props.history.push('/rates/promocodes/');
    } else {
      this.updateFormValue();
      this.setState({ isEdit: false });
    }
  };

  onEdit = () => {
    this.setState({ isEdit: true });
  };

  assignBlackoutPeriodsToForm = (blackoutPeriods: BlackoutPeriod[]) => {
    const form = this.form.controls.blackoutPeriods as FormArray;

    form.reset([], {});
    form.controls.length = 0;

    blackoutPeriods.forEach((blackoutPeriod) => {
      form.push(FormBuilder.group({
        validFrom: [ dayjs(blackoutPeriod.validFrom).format(DATE_FORMAT), [ Validators.required ] ],
        validTo: [ dayjs(blackoutPeriod.validTo).format(DATE_FORMAT), [ Validators.required ] ],
        id: [ blackoutPeriod.id ]
      }));
    });
  };

  addBlackoutPeriod = () => {
    const blackoutPeriods: BlackoutPeriod[] = this.state.blackoutPeriods;
    const index = this.state.blackoutPeriods.length;

    blackoutPeriods.push({
      validFrom: dayjs(),
      validTo: dayjs(),
      id: index
    });

    this.setState({ blackoutPeriods: blackoutPeriods });
  };

  removeBlackoutPeriod = (index: number) => {
    const blackoutPeriods: BlackoutPeriod[] = this.state.blackoutPeriods.filter((b: BlackoutPeriod) => b.id !== index);
    let newIndex = 0;
    const newBlackoutPeriods: BlackoutPeriod[] = [];
    blackoutPeriods.forEach((b) => {
      b.id = newIndex;
      newBlackoutPeriods.push(b);
      newIndex = newIndex + 1;
    });
    this.setState({ blackoutPeriods: newBlackoutPeriods });
  };

  getRentalDuration = (daysFrom: number, daysTo: number) => {
    if (daysFrom && daysTo) {
      return (
        <div className="form-control-static">
          {`${daysFrom} - ${daysTo}`}
        </div>
      );
    }
  };

  render() {
    if (!this.props.isNewCode && !this.props.selectedPromoCode) {
      return null;
    }

    const showForm = this.state.isEdit || this.props.isNewCode;
    const { t } = this.props;

    return (
      <main>
        <Breadcrumbs
          items={
            [
              { path: '/rates', label: 'Rates' },
              { path: '/rates/promocodes', label: 'Promo codes' },
              {
                path: null,
                label: this.props.isNewCode ? 'New' : this.props.selectedPromoCode && this.props.selectedPromoCode.code || ''
              }
            ]
          }
        />
        <div className="page-content">
          <div className="row justify-content-end">
            <div className="col-12 d-flex align-items-center justify-content-between">
              <h1
                className="mb-0">{`${!this.props.isNewCode && this.form.controls.promoCode.value ? this.form.controls.promoCode.value : t('page_title.add_new_promo_code')}`}</h1>
            </div>
          </div>
          <div className="mb-2"/>
          {this.props.selectedPromoCode && !showForm && this.props.user && (
            <div>
              {this.props.user && userCanAccessComponent(this.props.user, promoCodeEditRoles) ? (
                <SectionTitle
                  title={t('page_title.promo_code_details').toUpperCase()}
                  showButton
                  buttonName={t('b.edit_Generic edit button').toUpperCase()}
                  showDeleteButton
                  deleteButtonName={t('b.delete_Generic delete button').toUpperCase()}
                  onDeleteClick={() => this.setState({ isModalOpen: true })}
                  onEditClick={() => this.onEdit()}
                />
              ) : (
                <SectionTitle
                  title={t('page_title.promo_code_details').toUpperCase()}
                />
              )}
              <div className="d-flex row info-container">
                <label className="col-4 d-flex align-items-center justify-content-end">
                  {t('l.promo_code.promo_code')}:
                </label>
                <div
                  className="col-2 d-flex align-items-center justify-content-start">
                  {this.form.controls.promoCode.value}
                </div>
              </div>
              <div className="d-flex row info-container">
                <label className="col-4 d-flex align-items-center justify-content-end">
                  {t('l.promo_code.discount')}:
                </label>
                <div
                  className="col-2 d-flex align-items-center justify-content-start">
                  {this.form.controls.discountPercentage.value}
                </div>
              </div>
              <div className="d-flex row info-container">
                <label className="col-4 d-flex align-items-center justify-content-end">
                  {t('l.promo_code.comment')}:
                </label>
                <div
                  className="col-2 d-flex align-items-center justify-content-start">
                  {this.form.controls.comment.value}
                </div>
              </div>
              <div className="d-flex row info-container">
                <label className="col-4 d-flex align-items-center justify-content-end">
                  {t('l.promo_code.pickup_period')}:
                </label>
                <div className="col-6 d-flex align-items-center justify-content-start">
                  {this.form.controls.pickupFrom.value?.format('DD.MM.YYYY')}
                  {' - '}
                  {this.form.controls.pickupTo.value?.format('DD.MM.YYYY')}
                </div>
              </div>
              <div className="d-flex row info-container">
                <label className="col-4 d-flex align-items-center justify-content-end">
                  {t('l.promo_code.request_period')}:
                </label>
                <div className="col-6 d-flex align-items-center justify-content-start">
                  {this.form.controls.requestFrom.value?.format('DD.MM.YYYY')}
                  {' - '}
                  {this.form.controls.requestTo.value?.format('DD.MM.YYYY')}
                </div>
              </div>
              <div className="d-flex row info-container">
                <label className="col-4 d-flex align-items-center justify-content-end">
                  {t('l.promo_code.status')}:
                </label>
                <div className="col-6 d-flex align-items-center justify-content-start">
                  <StatusLabel status={this.props.selectedPromoCode.statusType}/>
                </div>
              </div>
              <div className="d-flex row info-container">
                <label className="col-4 d-flex align-items-center justify-content-end">
                  {t('l.promo_code.rental_days')}
                  <img src={infoIcon} className="i-info-icon" id="lor-tooltip"/>:</label>
                <Tooltip
                  className="dark-tooltip"
                  modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                  placement="bottom"
                  target="lor-tooltip"
                  autohide={false}
                >
                        <pre className="dark-tooltip-text">
                          {t('comment.promo_code.lor')}
                        </pre>
                </Tooltip>
                <div className="col-6 d-flex align-items-center justify-content-start">
                  {this.getRentalDuration(this.form.controls.daysFrom.value, this.form.controls.daysTo.value)}
                </div>
              </div>
              <SectionTitle title={t('section_title.promo_code.blackout_periods').toUpperCase()}/>
              <div>
                {this.getBlackoutPeriods()}
              </div>
              <SectionTitle title={t('section_title.promo_code.desks').toUpperCase()}/>
              <div className="d-flex row info-container">
                <div className="col-4"/>
                <div className="form-control-static">
                  {getLocationsList(this.props.selectedPromoCode.desks, this.props.desks,
                    true, this.props.history)}
                </div>
              </div>
              <SectionTitle title={t('section_title.promo_code.car_classes').toUpperCase()}/>
              <div className="d-flex row info-container">
                <div className="col-4"/>
                <div className="form-control-static">
                  {getCarClassesList(this.props.selectedPromoCode.carClasses, this.props.carClasses, t('v.all_car_classes'), this.props.history)}
                </div>
              </div>
              <SectionTitle title={t('section_title.promo_code.brokers').toUpperCase()}/>
              <div className="d-flex row info-container">
                <div className="col-4"/>
                <div className="form-control-static">
                  {getBrokers(this.props.selectedPromoCode.brokers, this.props.brokers, t('v.promo_codes.all_brokers'))}
                </div>
              </div>
              <SectionTitle title={t('section_title.promo_code.broker_rates').toUpperCase()}/>
              <div className="d-flex row info-container">
                <div className="col-4"/>
                <div className="form-control-static">
                  {getBrokerRates(
                    this.props.selectedPromoCode.brokerRates.map((promoBrokerRate) => ({ code: promoBrokerRate.brokerRateCode })),
                    this.props.brokerRates,
                    t('v.promo_codes.all_broker_rates'),
                    this.props.history
                  )}
                </div>
              </div>
            </div>
          )}
          <FieldGroup
            strict={false}
            control={this.form}
            render={() => (
              <form className="form-horizontal" onSubmit={(e) => this.onSubmit(e)}>
                {showForm && (
                  <><SectionTitle title={t('page_title.promo_code_details').toUpperCase()}/>
                    <div className="form-group row">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label className="mandatory-field">* </label>
                        <label>{t('l.promo_code.promo_code')}:</label>
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start single-row-item">
                        {
                          this.props.isNewCode
                            ? (
                              <FieldControl
                                name="promoCode"
                                render={TextInput}
                              />
                            ) : (
                              <div className="form-control-static">
                                {this.form.controls.promoCode.value}
                              </div>
                            )
                        }
                      </div>
                    </div>
                    <div className="form-group row">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label className="mandatory-field">* </label>
                        <label>{t('l.promo_code.discount')}:</label>
                      </div>
                      <div className="col-1 d-flex align-items-center justify-content-start single-row-item">
                        <div className="col padding-zero">
                          <FieldControl
                            name="discountPercentage"
                            render={TextInput}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="form-group row">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.promo_code.comment')}:</label>
                      </div>
                      <div className="col-4 d-flex align-items-center justify-content-start">
                        <FieldControl
                          name="comment"
                          render={TextInput}
                        />
                      </div>
                    </div>
                    <div className="form-group row">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label className="mandatory-field">* </label>
                        <label>{t('l.promo_code.pickup_period')}:</label>
                      </div>
                      <div className="col-2 d-flex row-gap">
                        <div className="col padding-zero">
                          <CalendarDateRangePicker
                            isStartDateInput
                            startDate={this.form.controls.pickupFrom.value}
                            endDate={this.form.controls.pickupTo.value}
                            onChange={(val) => this.onDateRangeChange(val, 'pickup')}
                            inputStyle={{ flex: '50%' }}
                            isInvalid={this.form.controls.pickupFrom.invalid && this.form.submitted}
                            id="1"
                          />
                        </div>
                        <div className="col padding-zero">
                          <CalendarDateRangePicker
                            isStartDateInput={false}
                            startDate={this.form.controls.pickupFrom.value}
                            endDate={this.form.controls.pickupTo.value}
                            onChange={(val) => this.onDateRangeChange(val, 'pickup')}
                            inputStyle={{ flex: '50%' }}
                            isInvalid={this.form.controls.pickupTo.invalid && this.form.submitted}
                            id="2"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="form-group row">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label className="mandatory-field">* </label>
                        <label>{t('l.promo_code.request_period')}:</label>
                      </div>
                      <div className="col-2 d-flex row-gap">
                        <div className="col padding-zero">
                          <CalendarDateRangePicker
                            isStartDateInput
                            startDate={this.form.controls.requestFrom.value}
                            endDate={this.form.controls.requestTo.value}
                            onChange={(val) => this.onDateRangeChange(val, 'request')}
                            inputStyle={{ flex: '50%' }}
                            isInvalid={this.form.controls.requestFrom.invalid && this.form.submitted}
                            id="3"
                          />
                        </div>
                        <div className="col padding-zero">
                          <CalendarDateRangePicker
                            isStartDateInput={false}
                            startDate={this.form.controls.requestFrom.value}
                            endDate={this.form.controls.requestTo.value}
                            onChange={(val) => this.onDateRangeChange(val, 'request')}
                            inputStyle={{ flex: '50%' }}
                            isInvalid={this.form.controls.requestTo.invalid && this.form.submitted}
                            id="4"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="form-group row">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.promo_code.rental_days')}
                          <img src={infoIcon} className="i-info-icon" id="lor-tooltip"/>:</label>
                        <Tooltip
                          className="dark-tooltip"
                          modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
                          placement="bottom"
                          target="lor-tooltip"
                          autohide={false}
                        >
                        <pre className="dark-tooltip-text">
                          {t('comment.promo_code.lor')}
                        </pre>
                        </Tooltip>

                      </div>
                      <div className="col-2 d-flex row-gap">
                        <div className="col padding-zero">
                          <FieldControl
                            name="daysFrom"
                            render={TextInput}
                          />
                        </div>
                        <div className="col padding-zero">
                          <FieldControl
                            name="daysTo"
                            render={TextInput}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="form-group row">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.promo_code.status')}:</label>
                      </div>
                      <div className="col-6 d-flex align-items-center justify-content-start">
                        <FieldControl
                          name="active"
                          render={(props) => <Checkbox {...props} id="statusField" className="mt-1">
                            {t('text.status.active')}
                          </Checkbox>}
                        />
                      </div>
                    </div>
                    <SectionTitle title={t('section_title.promo_code.blackout_periods').toUpperCase()}
                                  showButton
                                  onEditClick={this.addBlackoutPeriod}
                                  buttonName={t('b.promo_code.add_blackout_period').toUpperCase()}
                    />
                    {this.state.blackoutPeriods.length ? (
                      <FieldArray
                        name="blackoutPeriods"
                        strict={false}
                        render={({ controls }: any) => controls.map((control: AbstractControl, index: number) => {
                          return (
                            <FieldGroup
                              strict={false}
                              key={control.value.id}
                              control={control}
                              render={() => (
                                <div>
                                  <div key={control.value.id}>
                                    <div className="form-group row">
                                      <div className="col-4"/>
                                      <div className="col-4 d-flex row-gap">
                                        <div className="col padding-zero">
                                          <CalendarDateRangePicker
                                            isStartDateInput
                                            startDate={dayjs(control.value.validFrom)}
                                            endDate={dayjs(control.value.validTo)}
                                            onChange={(val) => this.onBlackoutDateRangeChange(val, control.value.id)}
                                            inputStyle={{ flex: '50%' }}
                                            isInvalid={
                                              control.value.validFrom.invalid ||
                                              (dayjs(control.value.validFrom).isBefore(dayjs(this.form.controls.pickupFrom.value)) ||
                                                dayjs(control.value.validFrom).isAfter(dayjs(this.form.controls.pickupTo.value))
                                              )}
                                            id="5"
                                          />
                                        </div>
                                        <div className="col padding-zero">
                                          <CalendarDateRangePicker
                                            isStartDateInput={false}
                                            startDate={dayjs(control.value.validFrom)}
                                            endDate={dayjs(control.value.validTo)}
                                            onChange={(val) => this.onBlackoutDateRangeChange(val, control.value.id)}
                                            inputStyle={{ flex: '50%' }}
                                            isInvalid={control.value.validTo.invalid ||
                                              (dayjs(control.value.validTo).isBefore(dayjs(this.form.controls.pickupFrom.value)) ||
                                                dayjs(control.value.validTo).isAfter(dayjs(this.form.controls.pickupTo.value))
                                              )}
                                            id="6"
                                          />
                                        </div>
                                        <div className="col-3 d-flex align-items-center justify-content-start">
                                          <button type="button" className="btn btn-danger mr-2"
                                                  onClick={() => {
                                                    this.removeBlackoutPeriod(control.value.id);
                                                  }}>
                                            {t('b.remove').toUpperCase()}</button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            />
                          );
                        })}
                      />
                    ) : (
                      <div className="d-flex row info-container">
                        <div className="col-4"/>
                        <div className="form-control-static text-muted">
                          {t('text.promo_code.no_blackout_periods_edit')}
                        </div>
                      </div>
                    )}
                    <SectionTitle title={t('section_title.promo_code.desks').toUpperCase()}/>
                    <div className="form-group row">
                      <div className="col-4"/>
                      <div className="col-6 d-flex align-items-center justify-content-start">
                        <div className="d-flex flex-column flex-wrap">
                          <div className="pb-1">
                            <FieldControl
                              strict={false}
                              name="locationsSelection"
                              render={(props) => <RadioButton {...props}
                                                              onClick={this.onAllLocationsSelect}
                                                              fieldValue="all"
                                                              id="loc_all">
                                {t('l.selection.all')}
                              </RadioButton>}
                            />
                            <FieldControl
                              strict={false}
                              name="locationsSelection"
                              render={(props) => <RadioButton {...props}
                                                              fieldValue="selection"
                                                              id="loc_sel">
                                {t('l.selection.selected')}
                              </RadioButton>}
                            />
                          </div>
                          {
                            this.form.controls.locationsSelection.value === 'selection'
                            && (
                              <FieldControl
                                name="locations"
                                render={(v) => (
                                  <MultiSelect
                                    {...v}
                                    selectedAttributeName="deskCode"
                                    sortAttributeName="name"
                                    handleValueChange={(val: any) => this.handleLocationSelectValueChange}
                                    filterAttributes={[ 'name', 'deskCode' ]}
                                    primaryAtr="name"
                                    secondaryAtr="deskCode"
                                  />
                                )}
                              />
                            )
                          }
                        </div>
                      </div>
                    </div>
                    <SectionTitle title={t('section_title.promo_code.car_classes').toUpperCase()}/>
                    <div className="form-group row">
                      <div className="col-4"/>
                      <div className="col-6 d-flex align-items-center justify-content-start">
                        <div className="d-flex flex-column flex-wrap">
                          <div className="pb-1">
                            <FieldControl
                              strict={false}
                              name="carClassSelection"
                              render={(props) =>
                                <RadioButton
                                  {...props}
                                  onClick={this.onAllCarClassesSelect}
                                  fieldValue="all"
                                  id="car_all"
                                >{t('l.selection.all')}
                                </RadioButton>
                              }/>
                            <FieldControl
                              strict={false}
                              name="carClassSelection"
                              render={(props) => <RadioButton {...props} fieldValue="selection" id="car_sel">
                                {t('l.selection.selected')}
                              </RadioButton>}
                            />
                          </div>
                          {
                            this.form.controls.carClassSelection.value === 'selection'
                            && (
                              <FieldControl
                                name="carClasses"
                                render={(v) => (
                                  <MultiSelect
                                    {...v}
                                    selectedAttributeName="acrissCode"
                                    sortAttributeName="acrissCode"
                                    handleValueChange={(val: any) => this.handleCarClassSelectValueChange(val)}
                                    filterAttributes={[ 'acrissCode' ]}
                                    primaryAtr="acrissCode"
                                  />
                                )}
                              />
                            )
                          }
                        </div>
                      </div>
                    </div>
                    <SectionTitle title={t('section_title.promo_code.brokers').toUpperCase()}/>
                    <div className="form-group row">
                      <div className="col-4"/>
                      <div className="col-6 d-flex align-items-center justify-content-start">
                        <div className="d-flex flex-column flex-wrap">
                          <div className="pb-1">
                            <FieldControl
                              strict={false}
                              name="brokerSelection"
                              render={(props) => (
                                <RadioButton
                                  {...props}
                                  onClick={this.onAllBrokersSelect}
                                  fieldValue="all"
                                  id="bro_all"
                                >
                                  {t('l.selection.all')}
                                </RadioButton>
                              )}
                            />
                            <FieldControl
                              strict={false}
                              name="brokerSelection"
                              render={(props) => (
                                <RadioButton
                                  {...props}
                                  fieldValue="selection"
                                  id="bro_sel"
                                >
                                  {t('l.selection.selected')}
                                </RadioButton>
                              )}
                            />
                          </div>
                          {
                            this.form.controls.brokerSelection.value === 'selection'
                            && (
                              <FieldControl
                                name="brokers"
                                render={(v) => (
                                  <MultiSelect
                                    {...v}
                                    selectedAttributeName="brokerId"
                                    sortAttributeName="name"
                                    handleValueChange={(val: any) => this.handleBrokerValueChange(val)}
                                    filterAttributes={[ 'name' ]}
                                    secondaryAtr="name"
                                  />
                                )}
                              />
                            )
                          }
                        </div>
                      </div>
                    </div>
                    <SectionTitle title={t('section_title.promo_code.broker_rates').toUpperCase()}/>
                    <div className="form-group row">
                      <div className="col-4"/>
                      <div className="col-6 d-flex align-items-center justify-content-start">
                        <div className="d-flex flex-column flex-wrap">
                          <div className="pb-1">
                            <FieldControl
                              strict={false}
                              name="brokerRateSelection"
                              render={(props) => (
                                <RadioButton
                                  {...props}
                                  onClick={this.onAllBrokerRatesSelect}
                                  fieldValue="all"
                                  id="bror_all"
                                >
                                  {t('l.selection.all')}
                                </RadioButton>
                              )}
                            />
                            <FieldControl
                              strict={false}
                              name="brokerRateSelection"
                              render={(props) => (
                                <RadioButton
                                  {...props}
                                  fieldValue="selection"
                                  id="bror_sel"
                                >
                                  {t('l.selection.selected')}
                                </RadioButton>
                              )}
                            />
                          </div>
                          {
                            this.form.controls.brokerRateSelection.value === 'selection'
                            && (
                              <FieldControl
                                name="brokerRates"
                                render={(v) => (
                                  <MultiSelect
                                    {...v}
                                    selectedAttributeName="code"
                                    sortAttributeName="name"
                                    handleValueChange={(val: any) => this.handleBrokerRateValueChange(val)}
                                    filterAttributes={[ 'name', 'code' ]}
                                    primaryAtr="code"
                                    secondaryAtr="name"
                                  />
                                )}
                              />
                            )
                          }
                        </div>
                      </div>
                    </div>
                    <div className="row mt-4">
                      <div className="col-12 text-right">
                        <button type="button" className="btn btn-outline-secondary btn-lg mr-2"
                                onClick={this.onCancel}>
                          {t('b.cancel_Generic cancel button translation').toUpperCase()}</button>
                        <button type="submit" className="btn btn-primary btn-lg" onClick={this.onSubmit}>
                          {t('b.save_Generic save button translation').toUpperCase()}
                        </button>
                      </div>
                    </div>
                  </>)}
              </form>
            )}
          />
          {this.props.selectedPromoCode && !showForm && (
            <>
              <SectionTitle title={t('section_title.audit').toUpperCase()}/>
              <div className="d-flex">
                <div className="col-8">
                  <div className="d-flex row info-container">
                    <label
                      className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.created_by')}: </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      {this.props.selectedPromoCode.createdBy ?? 'System'}{this.props.selectedPromoCode.createdAt ? ` @ ${formatDateTime(this.props.selectedPromoCode.createdAt)}` : ''}
                    </div>
                    <label
                      className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.modified_by')}: </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      {this.props.selectedPromoCode.modifiedBy ?? 'System'}{this.props.selectedPromoCode.modifiedAt ? ` @ ${formatDateTime(this.props.selectedPromoCode.modifiedAt)}` : ''}
                    </div>
                    <label
                      className="col-6 d-flex align-items-center justify-content-end">{t('l.car_model.log')}: </label>
                    <div className="col-4 d-flex align-items-center justify-content-start">
                      <Link to={`/rates/promocodes/${this.props.selectedPromoCode.code}/audit`}
                            className="btn btn-text-only"
                            target="_blank"
                            rel="noopener noreferrer">
                        {t('b.car_model.view_audit_log')}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <ConfirmationModal
          title={t('modal_text.promo_code.confirm_removal.title')}
          content={t('modal_text.promo_code.confirm_removal.content')}
          isOpen={this.state.isModalOpen}
          toggle={() => this.setState({ isModalOpen: false })}
          onConfirm={() => this.onDeleteConfirm()}
          onCancel={() => this.setState({ isModalOpen: false })}
        />
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  selectedPromoCode: state.rates.promoCodes.selectedPromoCode,
  postSuccess: state.rates.promoCodes.postSuccess,
  carClasses: state.core.carClasses.acrissCodes,
  desks: state.setup.desks.desks,
  brokerRates: state.setup.brokerRates.brokerRates,
  brokers: state.admin.supplierBrokers.supplierBrokers,
  lastFetch: state.rates.promoCodes.lastFetch,
  user: state.authentication.user,
  supplier: state.authentication.activeSupplier
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<{}, {}, PromoCodeActionTypes>
) => ({
  getSelectedPromoCode: (code: string) => dispatch(getPromoCode(code)),
  editPromoCode: (code: string, request: PromoCodeModel) => dispatch(editPromoCode(code, request)),
  addNewPromoCode: (request: PromoCodeModel) => dispatch(addNewPromoCode(request)),
  deletePromoCode: (code: string) => dispatch(deletePromoCode(code)),
  getAllCarClasses: () => dispatch(getAllCarClasses()),
  getDeskList: () => dispatch(getDeskList()),
  getBrokerList: () => dispatch(getSupplierBrokerList()),
  getBrokerRateList: () => dispatch(getBrokerRateList())
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(PromoCodeDetails));
